import React, { useEffect, useMemo, useState } from 'react'
import { portal } from '../portalApi.js'
import { fmt } from '../api.js'

// 계좌에 대한 설정은 전부 여기다 — 별칭·계좌유형·사업부·부서·제외.
//
// 예전에는 부서만 여기서 고치고 별칭·계좌유형·사업부는 자금일보 팝업에서 따로 고쳤다.
// 두 군데를 오가야 해서 어느 쪽이 최신인지 사람이 헷갈렸다. 자금일보의 «계좌 설정»은
// 이제 이 화면으로 건너온다.
//
// 같은 통장이 여러 자산으로 갈려 있으면(수집처가 바뀐 옛 자산, 통화별 외화) 형제
// 모두에 같이 쓴다 — 한쪽만 고치면 자금일보가 옛 자산을 보고 있어 안 바뀐 것처럼 보인다.
//
// 이것도 1차 분류다. 그 계좌를 여러 부서가 함께 쓰면 거래별로 고치면 되고, 그때 사람 손이
// 이긴다(사람 > 규칙 > 계좌·카드). 계좌는 서른몇 개라 한 번에 채울 만하다.

// fa/account_settings.py 의 CATEGORIES·BUSINESS_UNITS 와 같아야 한다.
// 다르면 화면에서 고른 값을 서버가 «모르는 계좌유형»으로 되돌려 보낸다.
const CATEGORIES = ['보통예금', 'MMT·RP', '외화', '선급금', '대출', '기타']
const BUSINESS_UNITS = ['에르모어', '링크맘', '기타']

export default function AccountDepts() {
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [q, setQ] = useState('')
  const [안붙은것만, set안붙은것만] = useState(false)
  const [적용중, set적용중] = useState(false)
  const [toast, setToast] = useState('')

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 2800) }

  const load = async () => {
    setErr('')
    try { setData(await portal.get('/api/meta/accounts')) }
    catch (e) { setErr(e.message) }
  }
  useEffect(() => { load() }, [])

  // 고친 칸 하나만 보낸다. 서버가 형제 자산까지 함께 쓴다.
  const 저장 = async (a, 바꿀것) => {
    setErr('')
    try {
      await portal.post('/api/meta/accounts', { assetId: a.assetId, ...바꿀것 })
      load()
    } catch (e) { setErr(e.message) }
  }

  const rows = useMemo(() => {
    if (!data) return []
    const 말 = q.trim()
    return data.accounts.filter((a) => {
      if (안붙은것만 && a.deptCode) return false
      if (!말) return true
      return [a.bank, a.accountNo, a.alias, a.nickname, a.deptName, a.businessUnit]
        .some((x) => (x || '').includes(말))
    })
  }, [data, q, 안붙은것만])

  const 요약 = useMemo(() => {
    if (!data) return { 전체: 0, 붙음: 0, 안붙은거래: 0 }
    let 붙음 = 0, 안붙은거래 = 0
    for (const a of data.accounts) {
      if (a.deptCode) 붙음 += 1
      else 안붙은거래 += a.count || 0
    }
    return { 전체: data.accounts.length, 붙음, 안붙은거래 }
  }, [data])

  const save = async (a, code) => {
    try { await portal.post('/api/meta/accounts', { assetId: a.assetId, deptCode: code }); load() }
    catch (e) { setErr(e.message) }
  }

  const 거래에적용 = async () => {
    set적용중(true)
    try {
      const r = await portal.post('/api/meta/accounts',
        { assetId: 'apply', apply: true, start: '2026-07-01' })
      notify(`계좌 ${r.계좌}개로 거래 ${fmt(r.붙임)}건에 부서를 붙였습니다`)
      load()
    } catch (e) { setErr(e.message) } finally { set적용중(false) }
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
        <input placeholder="은행·계좌번호·별칭·부서 검색" style={{ width: 230 }}
          value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="muted" style={{ fontSize: 12, display: 'inline-flex', gap: 5 }}>
          <input type="checkbox" checked={안붙은것만}
            onChange={(e) => set안붙은것만(e.target.checked)} />
          부서 안 붙은 것만
        </label>
        <button className="btn" disabled={적용중 || !요약.붙음} onClick={거래에적용}>
          {적용중 ? '붙이는 중…' : '거래에 부서 붙이기'}
        </button>
        <span className="muted" style={{ marginLeft: 'auto', fontSize: 12 }}>
          계좌 {요약.전체}개 중 부서 {요약.붙음}개
          {요약.안붙은거래 > 0 && ` · 부서 없는 거래 ${fmt(요약.안붙은거래)}건`}
        </span>
      </div>

      <div className="hint" style={{ marginBottom: 10 }}>
        계좌마다 부서를 정해 두면 그 계좌 거래에 한꺼번에 붙습니다. <b>1차 분류</b>라,
        한 계좌를 여러 부서가 함께 쓰면 데이터 화면에서 거래별로 고치시면 그게 이깁니다.
        <br />
        <b>사람이 고친 것 &gt; 규칙 &gt; 계좌·카드</b> 순입니다. 이미 규칙이나 사람이 붙인 부서는 덮지 않습니다.
        <br />
        같은 계좌가 수집처에 따라 다른 번호로 들어온 것은 <b>한 줄로 묶어</b> 보여 줍니다 — 부서를 정하면 양쪽에 함께 붙습니다.
      </div>

      {err && <div className="error-box">{err}</div>}
      {!err && !data && <div className="muted">불러오는 중…</div>}

      {data && (
        <div className="panel">
          <table className="acct-tb">
            <thead>
              <tr>
                <th className="l" style={{ width: 100 }}>은행</th>
                <th className="l" style={{ width: 150 }}>계좌번호</th>
                <th className="l" style={{ width: 150 }}>별칭</th>
                <th className="l" style={{ width: 110 }}>계좌유형</th>
                <th className="l" style={{ width: 100 }}>사업부</th>
                <th className="l" style={{ width: 160 }}>부서</th>
                <th style={{ width: 60 }}>자금일보</th>
                <th style={{ width: 60 }}>거래</th>
                <th style={{ width: 120 }}>입출금 합계</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} className="c muted">해당하는 계좌가 없습니다</td></tr>
              )}
              {rows.map((a) => (
                <tr key={a.assetId} style={a.excluded ? { opacity: .45 } : undefined}>
                  <td className="l">{a.bank}</td>
                  <td className="l" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {a.accountNo}
                    {a.currency && a.currency !== 'KRW' && (
                      <span className="muted" style={{ fontSize: 11 }}> · {a.currency}</span>
                    )}
                    {a.assetIds?.length > 1 && (
                      <div className="muted" style={{ fontSize: 11 }}
                        title={`수집처가 바뀌며 표기가 갈렸습니다: ${a.assetIds.join(', ')}`}>
                        표기 {a.assetIds.length}가지 · 부서는 함께 붙습니다
                      </div>
                    )}
                  </td>
                  <td className="l">
                    {/* 별칭 칸에는 «우리가 붙인 것»만 둔다. 은행이 준 닉네임을 같이 넣어
                        보여 줬더니, 다 채워진 것처럼 보이는데 실제 별칭은 비어 있어
                        자금일보에 아무것도 안 떴다. 닉네임은 흐린 바탕글로만 보여 준다 */}
                    <input defaultValue={a.alias || ''}
                      placeholder={a.nickname || '예: 유통 입/출금'}
                      title={a.nickname ? `은행이 준 이름: ${a.nickname}` : ''}
                      style={{ width: '100%' }}
                      onBlur={(e) => {
                        if ((e.target.value || '') === (a.alias || '')) return
                        저장(a, { alias: e.target.value })
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur() }} />
                  </td>
                  <td className="l">
                    <select value={a.category || ''} style={{ width: '100%' }}
                      onChange={(e) => 저장(a, { category: e.target.value })}>
                      <option value="">미지정</option>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="l">
                    <select value={a.businessUnit || ''} style={{ width: '100%' }}
                      onChange={(e) => 저장(a, { businessUnit: e.target.value })}>
                      <option value="">미지정</option>
                      {BUSINESS_UNITS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="l">
                    <select value={a.deptCode || ''} style={{ width: '100%' }}
                      onChange={(e) => save(a, e.target.value)}>
                      <option value="">미지정</option>
                      {data.departments.map((d) => (
                        <option key={d.code} value={d.code}>{d.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="c">
                    {/* 퇴직연금신탁처럼 우리 자산이 아닌 계좌는 자금일보에서 아예 뺀다 */}
                    <input type="checkbox" checked={!a.excluded}
                      title={a.excluded ? '자금일보에서 빠져 있습니다' : '자금일보에 셉니다'}
                      onChange={(e) => 저장(a, { excluded: !e.target.checked })} />
                  </td>
                  <td className="c">{a.count ? fmt(a.count) : <span className="muted">—</span>}</td>
                  <td className="r" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {a.amount ? fmt(Math.round(a.amount)) : <span className="muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}
